import React, { Fragment, useRef } from 'react'
import PropTypes, { arrayOf } from 'prop-types'
import { useIntl } from 'react-intl'
import Image from 'next/image'

import { SeoBlog } from '@/components/layout/Seo'
import { formatBlogDate } from '@/utils/date'
import { Link } from '@/components/common'
import { Layout, Rows, Section } from '@/components/layout'
import Popup from '@/components/blocks/Popup'
import BlogArticleListingSmall from '@/components/blocks/BlogArticleListingSmall'
import BlogArticleListingSmallHeader from '@/components/blocks/BlogArticleListingSmallHeader'
import BlogShare from '@/components/blocks/BlogShare'
import { getPopularPosts, getPostBySlugWithSEO, getBlogConfig } from '@/lib/blog/post'
import { APP_URL } from '@/config'
import { mapPostSeo } from '@/utils/seo'

import styles from './slug.module.scss'
import indexStyles from './index.module.scss'

const postsThatAlreadyIncludeFeaturedImage = {
  'small-business-tax-deductions-worksheet-part-1': true,
  'a-complete-guide-to-llc-accounting': true,
  'independent-contractor-expenses-spreadsheet': true,
  'avoid-paying-taxes-as-an-independent-contractor-legally': true,
  'what-does-net-30-mean': true,
  'calculate-retained-earnings': true,
  'accounting-formulas-small-business': true,
  'bookkeeping-for-freelancers': true,
  'how-long-does-an-ach-payment-take': true,
  'why-business-owners-are-moving-away-from-outsourced-bookkeeping': true,
  'net-cash-flow-formula': true,
  'independent-contractor-taxes': true,
  'need-to-know-about-freelance-retainers': true,
  '13-bookkeeper-software-features-your-small-business-needs': true,
  'the-new-neat-how-neat-is-evolving-to-help-small-business-owners': true,
}

export default function BlogPost({ post, popularPosts, seo }) {
  const { formatMessage } = useIntl()
  const formRef = useRef(null)
  return (
    <>
      <SeoBlog seoDetails={seo} />
      <Layout.Content topBorder>
        <Section alignCenter={false}>
          <div ref={formRef} className={indexStyles.articleSection}>
            <div className={indexStyles.post}>
              <Rows gap={32}>
                <h1 style={{ marginBottom: '-8px' }}>{post.title}</h1>
                <div>
                  <p className={styles.date}>{formatBlogDate(new Date(post.date))} |&nbsp;
                    {post.categories?.edges?.map(({ node }, index, edges) => (
                      <Fragment key={node.slug}>
                        <Link className={styles.categories} to={`/blog/category/${node.slug}`}>
                          {node.name}
                        </Link>
                        {index < edges.length - 1 && ', '}
                      </Fragment>
                    ))}
                  </p>
                </div>
                {!postsThatAlreadyIncludeFeaturedImage[post.slug] && <Image
                  alt={post.featuredImage?.node.altText}
                  height={post.featuredImage?.node.mediaDetails.height}
                  src={post.featuredImage?.node.sourceUrl}
                  style={{ height: 'auto' }}
                  width={post.featuredImage?.node.mediaDetails.width}
                />}
              </Rows>
              <div className={styles.content} dangerouslySetInnerHTML={{ __html: post.content }} />
              <BlogShare
                description={post.excerpt}
                link={`${APP_URL}/blog/${post.slug}`}
                photo={post.featuredImage?.node.sourceUrl}
                title={post.title}
              />
            </div>
            <div>
              <Rows gap={0}>
                <BlogArticleListingSmallHeader title={formatMessage({ id: 'pages.blog.popularArticles' })} />
                {popularPosts.map((popularPost, index) => (
                  <BlogArticleListingSmall
                    key={popularPost.slug}
                    date={formatBlogDate(new Date(popularPost.date))}
                    id={`featured-${popularPost.title}-${index}`}
                    img={popularPost.featuredImage?.node?.sourceUrl}
                    imgAlt={popularPost.featuredImage?.node?.altText}
                    link={`/blog/${popularPost.slug}`}
                    title={popularPost.title}
                  />
                ))}
              </Rows>
            </div>
          </div>

        </Section>
        <Popup postSlug={post.slug} />
      </Layout.Content>
    </>
  )
}

export async function getStaticProps(ctx) {
  const { post } = await getPostBySlugWithSEO(ctx.params.slug)

  if (!post) return { notFound: true }
  const { popular_posts: popularPostSlugs, revalidate_seconds: revalidateTime } = await getBlogConfig()

  const popularPosts = await getPopularPosts(popularPostSlugs)

  return {
    props: {
      post: post || null,
      popularPosts: popularPosts || [],
      seo: mapPostSeo(post),
    },
    revalidate: revalidateTime,
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

BlogPost.propTypes = {
  post: PropTypes.shape({
    title: PropTypes.string,
    excerpt: PropTypes.string,
    date: PropTypes.string,
    slug: PropTypes.string,
    content: PropTypes.string,
    categories: PropTypes.shape({
      node: PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
        slug: PropTypes.string,
      }),
    }),
    featuredImage: PropTypes.shape({
      node: PropTypes.shape({
        sourceUrl: PropTypes.string,
      }),
    }),
    seo: PropTypes.shape({
      fullHead: PropTypes.string,
    }),
  }),
  popularPosts: arrayOf(PropTypes.shape({})),
  seo: PropTypes.shape({}),
}
